/**
 * Every non-2xx response from the backend is normalized into an `ApiError`.
 * Consumers narrow via `instanceof` and `status`, never by pattern-matching
 * on message strings.
 */

/**
 * An error body as the API returns it.
 *
 * `detail` is a string for most failures and a list of field errors for a 422,
 * which is FastAPI's validation shape — so it is typed as unknown and narrowed
 * by `detailToMessage` rather than trusted to be printable.
 */
export interface ApiErrorBody {
  detail?: unknown
  code?: string
  [key: string]: unknown
}

/** A FastAPI validation entry: which field failed, and why. */
interface ValidationDetail {
  loc?: unknown[]
  msg?: string
}

function isValidationDetail(value: unknown): value is ValidationDetail {
  return typeof value === "object" && value !== null && "msg" in value
}

/**
 * One human sentence from whatever the API put in `detail`.
 *
 * Returns null when there is nothing readable, so the caller falls back to the
 * status text instead of printing a placeholder. Interpolating the raw value
 * is what produces "[object Object]" in front of a user.
 */
export function detailToMessage(detail: unknown): string | null {
  if (typeof detail === "string") return detail.trim() || null

  if (Array.isArray(detail)) {
    const parts = detail.filter(isValidationDetail).map((entry) => {
      const field = Array.isArray(entry.loc) ? entry.loc.filter((p) => p !== "body").at(-1) : null
      const reason = entry.msg ?? "is not valid"
      return typeof field === "string" ? `${field}: ${reason}` : reason
    })
    return parts.length > 0 ? parts.join("; ") : null
  }

  if (isValidationDetail(detail)) return detail.msg ?? null
  return null
}

export class ApiError extends Error {
  readonly status: number
  readonly code?: string | undefined
  readonly body?: ApiErrorBody | undefined
  /** Seconds from Retry-After header on 429 responses; null otherwise. */
  readonly retryAfterSeconds: number | null

  constructor(
    status: number,
    message: string,
    opts?: { code?: string; body?: ApiErrorBody; retryAfterSeconds?: number | null },
  ) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.code = opts?.code
    this.body = opts?.body
    this.retryAfterSeconds = opts?.retryAfterSeconds ?? null
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }
  get isForbidden(): boolean {
    return this.status === 403
  }
  get isNotFound(): boolean {
    return this.status === 404
  }
  get isConflict(): boolean {
    return this.status === 409
  }
  get isRateLimited(): boolean {
    return this.status === 429
  }
  get isServer(): boolean {
    return this.status >= 500
  }
}

export class AbortedError extends Error {
  constructor(message = "Request aborted") {
    super(message)
    this.name = "AbortedError"
  }
}
